// Phase 7.1 — Tasks page.
// Phase 10B — restyle to AutoMind 2050 design language.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, XCircle, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import { tasksApi } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { PageHeader } from '../../components/ui/page-header';
import { TabBar } from '../../components/ui/tab-bar';
import { DataCard } from '../../components/ui/data-card';
import { MetricCard } from '../../components/ui/metric-card';
import { SectionGrid } from '../../components/ui/section-grid';
import { StatusPill } from '../../components/ui/status-pill';
import { PriorityBadge } from '../../components/ui/priority-badge';
import { EmptyState } from '../../components/ui/empty-state';
import {
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
  TASK_SOURCE_LABELS,
} from './constants';

function shortId(id) {
  if (!id) return '';
  return String(id).slice(0, 8);
}

// Relative due-time formatter ("in 12m", "overdue 3h", or short date for far-out).
function formatDueRelative(due) {
  if (!due) return '—';
  const d = new Date(due);
  if (isNaN(d.getTime())) return String(due);
  const now = Date.now();
  const diffMs = d.getTime() - now;
  const absMin = Math.round(Math.abs(diffMs) / 60000);
  const overdue = diffMs < 0;
  if (absMin < 60) {
    return overdue ? `overdue ${absMin}m` : `in ${absMin}m`;
  }
  const absHr = Math.round(absMin / 60);
  if (absHr < 24) {
    return overdue ? `overdue ${absHr}h` : `in ${absHr}h`;
  }
  const absDay = Math.round(absHr / 24);
  if (absDay < 14) {
    return overdue ? `overdue ${absDay}d` : `in ${absDay}d`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const STATUS_TONE = {
  open:        { tone: 'info' },
  in_progress: { tone: 'gold' },
  completed:   { tone: 'success' },
  dismissed:   { tone: 'neutral' },
};

// Tab definitions for queue filtering.
const QUEUE_TABS = [
  { id: 'mine',      label: 'My queue' },
  { id: 'dealer',    label: 'Dealer queue' },
  { id: 'completed', label: 'Completed' },
  { id: 'dismissed', label: 'Dismissed' },
];

const PRIORITY_OPTIONS = [
  { value: '',       label: 'All priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high',   label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low',    label: 'Low' },
];

export default function Tasks() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [queue, setQueue] = useState('mine');
  const [priorityFilter, setPriorityFilter] = useState('');

  // Derive backend status filter from active queue tab.
  const queueStatus = useMemo(() => {
    if (queue === 'completed') return 'completed';
    if (queue === 'dismissed') return 'dismissed';
    return 'open';
  }, [queue]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100, offset: 0 };
      if (queueStatus) params.status = queueStatus;
      if (priorityFilter) params.priority = priorityFilter;
      const res = await tasksApi.list(params);
      setItems(res?.items || []);
    } catch (err) {
      setError(err);
      toast.error(err?.message || 'Failed to load tasks');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [queueStatus, priorityFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Client-side filter for "My queue" vs "Dealer queue" — assignee_user_id matches current user.
  const visibleItems = useMemo(() => {
    if (queue === 'mine' && user?.id) {
      return items.filter((t) => !t.assignee_user_id || t.assignee_user_id === user.id);
    }
    return items;
  }, [items, queue, user]);

  // KPIs from currently loaded items.
  const kpis = useMemo(() => {
    // For total/urgent/high we want the OPEN counts. If on a non-open tab, all.items reflect that tab.
    const open = items.filter((t) => t.status === 'open' || t.status === 'in_progress');
    const urgent = open.filter((t) => t.priority === 'urgent').length;
    const high = open.filter((t) => t.priority === 'high').length;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const completedToday = items.filter((t) => {
      if (t.status !== 'completed') return false;
      const at = t.completed_at || t.updated_at || t.resolved_at;
      if (!at) return false;
      return new Date(at).getTime() >= todayStart.getTime();
    }).length;
    return {
      totalOpen: open.length,
      urgent,
      high,
      completedToday,
    };
  }, [items]);

  const handleComplete = async (task) => {
    setBusyId(task.id);
    try {
      await tasksApi.complete(task.id);
      toast.success('Task completed');
      await fetchTasks();
    } catch (err) {
      toast.error(err?.message || 'Failed to complete task');
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (task) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Optional reason for dismissing this task?') || '';
    setBusyId(task.id);
    try {
      await tasksApi.dismiss(task.id, reason.trim() || undefined);
      toast.success('Task dismissed');
      await fetchTasks();
    } catch (err) {
      toast.error(err?.message || 'Failed to dismiss task');
    } finally {
      setBusyId(null);
    }
  };

  const resolved = (t) => t.status === 'completed' || t.status === 'dismissed';

  const headerActions = (
    <button
      type="button"
      onClick={fetchTasks}
      disabled={loading}
      className="btn-2050 btn-secondary"
      data-testid="tasks-refresh"
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <RefreshCw className="w-3.5 h-3.5" />
      )}
      Refresh
    </button>
  );

  const renderRows = () => {
    if (error) {
      return (
        <EmptyState
          title="Couldn't load tasks"
          description="Unable to load right now."
        />
      );
    }
    if (visibleItems.length === 0) {
      return (
        <EmptyState
          title="No tasks"
          description="Nothing matches the current filters."
        />
      );
    }
    return (
      <div className="overflow-x-auto -mx-6">
        <table className="w-full text-sm" data-testid="tasks-table">
          <thead>
            <tr className="text-xs uppercase tracking-[0.16em] text-ink-500 bg-ink-50/60 dark:bg-ink-900/40">
              <th className="text-left px-6 py-3 font-medium">Priority</th>
              <th className="text-left px-6 py-3 font-medium">Type</th>
              <th className="text-left px-6 py-3 font-medium">Title</th>
              <th className="text-left px-6 py-3 font-medium">Due</th>
              <th className="text-left px-6 py-3 font-medium">Linked</th>
              <th className="text-left px-6 py-3 font-medium">Source</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
              <th className="text-right px-6 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
            {visibleItems.map((t) => {
              const statusInfo = STATUS_TONE[t.status] || { tone: 'neutral' };
              return (
                <tr
                  key={t.id}
                  className="row-2050 hover:bg-ink-50/60 dark:hover:bg-ink-900/40 align-top"
                  data-testid={`tasks-row-${t.id}`}
                >
                  <td className="px-6 py-3">
                    <PriorityBadge priority={t.priority} />
                  </td>
                  <td className="px-6 py-3 text-ink-500">
                    {TASK_TYPE_LABELS[t.task_type] || t.task_type}
                  </td>
                  <td className="px-6 py-3">
                    <div className="font-medium text-ink-900 dark:text-ink-50">
                      {t.title}
                    </div>
                    {t.description && (
                      <div className="text-xs text-ink-500 mt-0.5 line-clamp-2">
                        {t.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3 nums whitespace-nowrap text-ink-500">
                    {formatDueRelative(t.due_at)}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-col gap-1">
                      {t.lead_id && (
                        <span className="inline-flex items-center gap-1 text-xs nums text-ink-500">
                          <User className="w-3 h-3" /> {shortId(t.lead_id)}
                        </span>
                      )}
                      {t.call_id && (
                        <span className="inline-flex items-center gap-1 text-xs nums text-ink-500">
                          <Phone className="w-3 h-3" /> {shortId(t.call_id)}
                        </span>
                      )}
                      {!t.lead_id && !t.call_id && (
                        <span className="text-xs text-ink-400">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-xs text-ink-500">
                    {TASK_SOURCE_LABELS[t.source] || t.source}
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill
                      tone={statusInfo.tone}
                      label={TASK_STATUS_LABELS[t.status] || t.status}
                    />
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    {!resolved(t) ? (
                      <div className="inline-flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleComplete(t)}
                          disabled={busyId === t.id}
                          className="btn-2050 btn-secondary"
                          data-testid={`tasks-complete-${t.id}`}
                        >
                          {busyId === t.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <CheckCircle2 className="w-3 h-3" />
                          )}
                          Complete
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDismiss(t)}
                          disabled={busyId === t.id}
                          className="btn-2050 btn-ghost"
                          data-testid={`tasks-dismiss-${t.id}`}
                        >
                          <XCircle className="w-3 h-3" />
                          Dismiss
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-ink-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Operations"
        title="Tasks"
        description="Open follow-ups, callbacks, and QA reviews."
        actions={headerActions}
      />

      <SectionGrid cols={4}>
        <MetricCard label="Total open"      value={kpis.totalOpen}      helperText="Open + in progress" />
        <MetricCard label="Urgent"          value={kpis.urgent}         helperText="Need attention" />
        <MetricCard label="High"            value={kpis.high}           helperText="High priority" />
        <MetricCard label="Completed today" value={kpis.completedToday} helperText="Resolved today" />
      </SectionGrid>

      <TabBar value={queue} onChange={setQueue} items={QUEUE_TABS} />

      <DataCard
        title="Tasks"
        description={`${visibleItems.length} shown`}
        actions={
          <select
            className="h-8 text-sm rounded-md border border-ink-200/70 dark:border-ink-800/70 bg-white/40 dark:bg-ink-900/30 px-2 focus-2050 outline-none"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            data-testid="tasks-filter-priority"
          >
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        }
        bodyClassName="p-0"
        loading={loading && items.length === 0}
      >
        {renderRows()}
      </DataCard>
    </div>
  );
}
